import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { useRouteMatch } from 'react-router-dom';
import {
  Tab,
  Alert,
  Chip,
  TextField,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { Formik, useFormikContext } from 'formik';
import format from 'string-template';

import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import FirebaseContext from '../../../../../context/FirebaseContext';
import ExternalCoachContext, { withExternalCoachContextReady } from '../../../../context/ExternalCoachContext';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import useStorage, { StorageProcessState } from '../../../../../hooks/useStorage';
import useToast from '../../../../hooks/useToast';
import useLogger from '../../../../../hooks/useLogger';
import Form, { FormType } from '../../../../Model/Form';
import { getPostPaymentFormComparator } from '../ProductsConfig/postPaymentFormSelection';
import {
  AddButton,
  EditButton,
  PrimaryButton,
  SaveButton,
} from '../../../../../components/Button/ActionButtons';
import { isEnableLeadsFormManagementForCoaches } from '../../../../../utils/postHog';
import fieldName from '../../../../components/FormBuilder/formFields';
import QuestionType, {
  LeadQuestionType, PostPaymentQuestionType,
} from '../../../../components/FormBuilder/questionType';
import FieldArray from '../../../../components/FormBuilder/components/FieldArray';
import QuestionElement from '../../../../components/FormBuilder/components/FormQuestions/questionElement';
import { storagePaths, pathPlaceholder } from '../../../../../utils/firebasePaths';

import texts from './texts.json';
import { FormSettingsEvent, FormTab, tabFormType } from './utils';
import {
  Container,
  HeaderContainer,
  Title,
  SubTitle,
  StyledTabs,
  EmptyStateAlert,
  FooterContainer,
  FormListContainer,
  FormRow,
  FormRowName,
  FormRowActions,
  ListActionsContainer,
} from './styles';

const addOptionsByFormType = {
  [FormType.LEAD]: Object.values(QuestionType).filter((type) => LeadQuestionType.includes(type)),
  [FormType.POST_PAYMENT]: Object.values(QuestionType).filter((type) => PostPaymentQuestionType.includes(type)),
};

const DirtyObserver = ({ onDirtyChange }) => {
  const { dirty } = useFormikContext();

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  return null;
};

const FormSettings = () => {
  const { params: { userId: coachId } } = useRouteMatch();
  const { showToast } = useToast();
  const { getJSONData, uploadJSON, deleteAttachment } = useStorage();
  const { logEvent } = useLogger();
  const isComponentMountedRef = useComponentMounted();
  const { userId } = useContext(LoggedInUserContext);
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { coachDoc } = useContext(ExternalCoachContext);

  const [activeTab, setActiveTab] = useState(
    isEnableLeadsFormManagementForCoaches() ? FormTab.LEAD : FormTab.POST_PAYMENT,
  );
  const [pendingTab, setPendingTab] = useState(null);
  const [formDoc, setFormDoc] = useState(null);
  const [fields, setFields] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSwitchDialog, setShowSwitchDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newFormName, setNewFormName] = useState('');
  // Form awaiting the "really make this the default?" confirmation, or null.
  const [pendingDefaultFormId, setPendingDefaultFormId] = useState(null);
  // A swap the server confirmed in this session, applied over the coach doc pointer —
  // the same shape the coach Forms page uses. The coach doc is not a live document here
  // (see below), so this is what actually moves the Default chip on confirmation.
  const [confirmedDefaultFormId, setConfirmedDefaultFormId] = useState(null);

  // Cache loaded fields per lead tab / per PPF form id to avoid re-fetching
  const formCacheRef = useRef({});

  const storagePath = format(storagePaths.COACH_FORM_COLLECTION, {
    [pathPlaceholder.COACH_ID]: coachId,
  });

  // Live PPF list: firestorter keeps the snapshot listener attached for as long
  // as this (observer) component renders the collection, so new forms show up
  // without any manual refetch. The default pointer does NOT come from here —
  // it lives on the coach doc, which is refreshed explicitly (see below).
  const ppfFormsCollection = useMemo(
    () => Form.getCoachFormsCollection(coachId, FormType.POST_PAYMENT),
    [coachId],
  );

  // The coach's default PPF is a pointer on their coach doc, which ExternalCoachContext
  // loads once (BaseDocument.init = a one-shot fetch + ready). Pointer moves made by the
  // server therefore do not repaint this screen on their own, so a confirmed swap wins
  // over the loaded value and every mutation below also refreshes the doc itself.
  const defaultFormId = confirmedDefaultFormId ?? (coachDoc?.defaultPostPaymentFormId || '');

  // Re-reads the coach doc so `defaultFormId` reflects a server-side pointer change.
  // firestorter's fetch() throws while a fetch is in flight or a realtime listener is
  // attached, so guard both — the same shape as BaseDocument.initCollection.
  const refreshCoachDoc = useCallback(async () => {
    if (!coachDoc || coachDoc.isLoading || coachDoc.isActive) {
      return;
    }
    try {
      await coachDoc.fetch();
    } catch (error) {
      // Best effort: the server write already succeeded, so a failed refresh only means
      // the pointer stays stale until the next page load — never a failed mutation.
    }
  }, [coachDoc]);

  // Pinnable = not the master template; default form first, then alphabetically
  // for a stable list. Computed during render so the observer tracks the reads.
  const ppfForms = ppfFormsCollection.docs
    .filter((form) => !form.isDefault)
    .sort(getPostPaymentFormComparator(defaultFormId));

  const loadLeadForm = useCallback(async () => {
    const cached = formCacheRef.current[FormTab.LEAD];
    if (cached) {
      setFormDoc(cached.doc);
      setFields(cached.fields);
      setHasUnsavedChanges(false);
      return;
    }

    setIsLoading(true);
    setFormDoc(null);
    setFields(null);
    setHasUnsavedChanges(false);

    try {
      const forms = await Form.getFormsByCoachId(coachId, FormType.LEAD);
      const doc = forms?.[0] || null;
      if (!isComponentMountedRef.current) return;

      if (!doc) {
        setIsLoading(false);
        formCacheRef.current[FormTab.LEAD] = { doc: null, fields: null };
        return;
      }
      const formData = await getJSONData(`${storagePath}/${doc.id}`);
      if (!isComponentMountedRef.current) return;

      const loadedFields = formData?.fields ?? [];
      formCacheRef.current[FormTab.LEAD] = { doc, fields: loadedFields };

      setFormDoc(doc);
      setFields(loadedFields);
      setIsLoading(false);
    } catch (error) {
      if (isComponentMountedRef.current) {
        showToast(texts.loadError, { error: true });
        setIsLoading(false);
      }
    }
  }, [coachId, getJSONData, isComponentMountedRef, showToast, storagePath]);

  useEffect(() => {
    if (activeTab === FormTab.POST_PAYMENT) {
      // The list itself is live — switching tabs only needs the editor closed.
      setFormDoc(null);
      setFields(null);
      setHasUnsavedChanges(false);
    } else {
      loadLeadForm();
    }
  }, [activeTab, loadLeadForm]);

  const handleEditForm = useCallback(async (doc) => {
    const cached = formCacheRef.current[doc.id];
    if (cached) {
      setFormDoc(doc);
      setFields(cached.fields);
      setHasUnsavedChanges(false);
      return;
    }

    setIsLoading(true);
    try {
      const formData = await getJSONData(`${storagePath}/${doc.id}`);
      if (!isComponentMountedRef.current) return;

      const loadedFields = formData?.fields ?? [];
      formCacheRef.current[doc.id] = { fields: loadedFields };
      setFormDoc(doc);
      setFields(loadedFields);
      setHasUnsavedChanges(false);
      setIsLoading(false);
    } catch (error) {
      if (isComponentMountedRef.current) {
        showToast(texts.loadError, { error: true });
        setIsLoading(false);
      }
    }
  }, [getJSONData, isComponentMountedRef, showToast, storagePath]);

  const handleBackToList = useCallback(() => {
    if (hasUnsavedChanges) {
      setPendingTab(FormTab.POST_PAYMENT);
      setShowSwitchDialog(true);
      return;
    }
    setFormDoc(null);
    setFields(null);
  }, [hasUnsavedChanges]);

  const handleTabChange = useCallback((_, newTab) => {
    if (hasUnsavedChanges) {
      setPendingTab(newTab);
      setShowSwitchDialog(true);
    } else {
      setActiveTab(newTab);
    }
  }, [hasUnsavedChanges]);

  const handleConfirmSwitch = useCallback(() => {
    setShowSwitchDialog(false);
    setHasUnsavedChanges(false);
    if (pendingTab === activeTab) {
      // Back-to-list confirmation on the same tab
      setFormDoc(null);
      setFields(null);
    } else {
      setActiveTab(pendingTab);
    }
    setPendingTab(null);
  }, [pendingTab, activeTab]);

  const handleCancelSwitch = useCallback(() => {
    setShowSwitchDialog(false);
    setPendingTab(null);
  }, []);

  const handleSetDefault = useCallback(async (formId) => {
    setIsLoading(true);
    try {
      const response = await remote('forms-setDefaultPostPaymentForm', { formId });
      const { success, error } = await response.json();
      if (!success) {
        // A validation failure (e.g. a zod 400) can reply with a non-string error shape;
        // only surface it directly when it's actually a message meant for display.
        throw new Error(typeof error === 'string' ? error : texts.setDefaultError);
      }
      // The endpoint moved the pointer on the coach doc: paint the confirmed value and
      // pull the doc itself back in, so the chip, the list order and any later read of
      // `defaultFormId` all agree with what the server now stores.
      setConfirmedDefaultFormId(formId);
      await refreshCoachDoc();
      showToast(texts.setDefaultSuccess);
    } catch (error) {
      if (isComponentMountedRef.current) {
        showToast(error.message, { error: true });
      }
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [remote, showToast, isComponentMountedRef, refreshCoachDoc]);

  const handleConfirmSetDefault = useCallback(() => {
    const formId = pendingDefaultFormId;
    setPendingDefaultFormId(null);
    handleSetDefault(formId);
  }, [pendingDefaultFormId, handleSetDefault]);

  const handleCreateForm = useCallback(async () => {
    const formName = newFormName.trim();
    if (!formName) {
      showToast(texts.createNameRequired, { error: true });
      return;
    }

    setShowCreateDialog(false);
    setIsLoading(true);
    // forms-onFormCreated promotes a coach's first PPF straight to their live default, so the
    // success copy needs to reflect that instead of claiming every new form starts inert.
    // `!defaultFormId` (not ppfForms.length) is the right signal: ppfForms excludes archived
    // forms, so a coach whose only prior form was archived would otherwise be told their new
    // form was "automatically set as default" when it wasn't — defaultFormId is what actually
    // tracks promotion.
    const isFirstForm = !defaultFormId;
    // Set once the storage JSON upload has gone through, so the catch block knows there's a
    // (now possibly orphaned) storage file to clean up if the Firestore write below fails.
    let uploadedStoragePath;
    try {
      // Coach-created forms always start from the master PPF template — never
      // from one of the coach's own forms, which may have drifted — so the
      // mandatory system questions the post-payment processing depends on are
      // guaranteed to be present.
      const template = await Form.getMasterPostPaymentTemplate();
      const templateStoragePath = template && format(storagePaths.COACH_FORM_COLLECTION, {
        [pathPlaceholder.COACH_ID]: template.coachId,
      });
      const templateData = template && await getJSONData(`${templateStoragePath}/${template.id}`);
      if (!templateData?.fields?.length) {
        showToast(texts.createTemplateMissing, { error: true });
        return;
      }
      const docData = {
        coach: coachDoc?.name || '',
        coachId,
        formName,
        type: FormType.POST_PAYMENT,
        createdBy: userId,
        isArchived: false,
      };

      // Reserve the doc id and upload the storage JSON to it BEFORE the Firestore doc exists,
      // then create the doc last. The server's onFormCreated trigger can promote a brand-new
      // doc to the coach's default within milliseconds of it existing, so creating the doc
      // first (as before) risked a fast promotion followed by a rollback delete that left
      // defaultPostPaymentFormId dangling. With the doc created last AND the upload outcome
      // actually checked below (uploadJSON resolves with a FAILED state instead of throwing —
      // it never rejects), a real failure at either step now leaves at most an orphaned,
      // unreferenced storage file behind — harmless — never a promoted-then-deleted doc, and
      // never a doc pointing at content that was never actually written.
      const newFormId = Form.reserveFormId();
      const uploadResult = await uploadJSON(
        JSON.stringify({ ...docData, fields: templateData.fields }),
        storagePath,
        newFormId,
      );
      if (uploadResult?.state !== StorageProcessState.SUCCESS) {
        throw new Error(texts.createUploadError);
      }
      uploadedStoragePath = `${storagePath}/${newFormId}.json`;
      await Form.addFormWithId(newFormId, docData);

      if (!isComponentMountedRef.current) return;

      // The forms collection is observed and picks the new doc up itself, but the coach
      // doc is not — refresh it so a first-form promotion by forms-onFormCreated shows as
      // the Default chip, and so the next create reads a current `isFirstForm`.
      await refreshCoachDoc();

      showToast(isFirstForm ? texts.createSuccessFirst : texts.createSuccess);
      setNewFormName('');
      logEvent(FormSettingsEvent.CREATED, {
        coach_id: coachId,
        form_id: newFormId,
        created_by_user_id: userId,
      });
    } catch (error) {
      if (isComponentMountedRef.current) {
        showToast(texts.createError, { error: true });
      }
      // uploadedStoragePath is only set once the upload has been confirmed SUCCESS, so this
      // only fires when the doc-create write itself failed afterward — the one case that
      // leaves an orphaned storage file. deleteAttachment is already best-effort (it catches
      // and reports internally, never throws), so it's fired without blocking the toast above.
      if (uploadedStoragePath) {
        deleteAttachment(uploadedStoragePath);
      }
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    newFormName,
    coachDoc,
    coachId,
    userId,
    getJSONData,
    uploadJSON,
    deleteAttachment,
    storagePath,
    showToast,
    logEvent,
    isComponentMountedRef,
    defaultFormId,
    refreshCoachDoc,
  ]);

  const handleSave = useCallback(async (values, actions) => {
    if (!formDoc) return;

    const formType = tabFormType[activeTab];

    setIsSaving(true);

    try {
      const existingFormData = await getJSONData(`${storagePath}/${formDoc.id}`);
      const updatedFields = values[fieldName.FIELDS].map(({ isDefault, ...rest }) => rest);
      const formConfigString = JSON.stringify({
        ...existingFormData,
        [fieldName.FIELDS]: updatedFields,
      });

      await uploadJSON(formConfigString, storagePath, formDoc.id);

      if (isComponentMountedRef.current) {
        if (activeTab === FormTab.LEAD) {
          formCacheRef.current[FormTab.LEAD] = { doc: formDoc, fields: updatedFields };
        } else {
          formCacheRef.current[formDoc.id] = { fields: updatedFields };
        }
        showToast(texts.saveSuccess);
        setIsSaving(false);
        setHasUnsavedChanges(false);
        actions.setSubmitting(false);
        actions.resetForm({ values });

        logEvent(FormSettingsEvent.SAVED, {
          coach_id: coachId,
          form_type: formType,
          saved_by_user_id: userId,
          saved_at: new Date(),
        });
      }
    } catch (error) {
      if (isComponentMountedRef.current) {
        showToast(texts.saveError, { error: true });
        setIsSaving(false);
        actions.setSubmitting(false);
      }
    }
  }, [
    activeTab,
    coachId,
    formDoc,
    getJSONData,
    isComponentMountedRef,
    logEvent,
    showToast,
    storagePath,
    uploadJSON,
    userId,
  ]);

  const formType = tabFormType[activeTab];
  const initialValues = { [fieldName.FIELDS]: fields ?? [] };
  const isPPFTab = activeTab === FormTab.POST_PAYMENT;
  const showPPFList = isPPFTab && !formDoc;
  const showEditor = fields !== null && !!formDoc;

  const renderPPFList = () => {
    if (!ppfFormsCollection.isLoaded) {
      return null;
    }
    if (ppfForms.length === 0) {
      return (
        <>
          <EmptyStateAlert severity="info">
            {texts.ppfEmptyState}
          </EmptyStateAlert>
          <ListActionsContainer>
            <AddButton onClick={() => setShowCreateDialog(true)}>
              {texts.createButton}
            </AddButton>
          </ListActionsContainer>
        </>
      );
    }
    return (
      <>
        <FormListContainer>
          {ppfForms.map((form) => (
            <FormRow key={form.id}>
              <FormRowName>
                {form.formName}
                {form.id === defaultFormId && <Chip size="small" color="primary" label={texts.defaultChip} />}
              </FormRowName>
              <FormRowActions>
                <EditButton onClick={() => handleEditForm(form)}>
                  {texts.editButton}
                </EditButton>
                {form.id !== defaultFormId && (
                  <PrimaryButton size="medium" onClick={() => setPendingDefaultFormId(form.id)}>
                    {texts.setDefaultButton}
                  </PrimaryButton>
                )}
              </FormRowActions>
            </FormRow>
          ))}
        </FormListContainer>
        <ListActionsContainer>
          <AddButton onClick={() => setShowCreateDialog(true)}>
            {texts.createButton}
          </AddButton>
        </ListActionsContainer>
      </>
    );
  };

  return (
    <Container>
      <LoadingOverlay isLoading={isLoading || isSaving || (showPPFList && ppfFormsCollection.isLoading)} />
      <HeaderContainer>
        <Title>{texts.title}</Title>
        <SubTitle>{texts.subtitle}</SubTitle>
      </HeaderContainer>

      <StyledTabs value={activeTab} onChange={handleTabChange}>
        {isEnableLeadsFormManagementForCoaches() && (
          <Tab label={texts.tabs.leadForm} value={FormTab.LEAD} />
        )}
        <Tab label={texts.tabs.postPaymentForm} value={FormTab.POST_PAYMENT} />
      </StyledTabs>

      {showPPFList && renderPPFList()}

      {!isPPFTab && !isLoading && fields === null && (
        <EmptyStateAlert severity="info">
          {texts.emptyState}
        </EmptyStateAlert>
      )}

      {showEditor && (
        <>
          {isPPFTab && (
            <ListActionsContainer>
              <PrimaryButton size="medium" icon={<ArrowBack />} onClick={handleBackToList}>
                {texts.backToList}
              </PrimaryButton>
              <SubTitle>{formDoc.formName}</SubTitle>
            </ListActionsContainer>
          )}
          <Formik
            initialValues={initialValues}
            onSubmit={handleSave}
            enableReinitialize
          >
            {({ values, isSubmitting, handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <DirtyObserver onDirtyChange={setHasUnsavedChanges} />

                <FieldArray
                  questions={values[fieldName.FIELDS]}
                  fieldName={fieldName.FIELDS}
                  addOptions={addOptionsByFormType[formType]}
                  elementArray={QuestionElement}
                  formType={formType}
                  isCoachView
                />

                <FooterContainer>
                  {hasUnsavedChanges && (
                    <Alert severity="warning">
                      {texts.unsavedBanner}
                    </Alert>
                  )}
                  <SaveButton
                    type="submit"
                    disabled={isSubmitting || !hasUnsavedChanges}
                    $fitToContent
                  >
                    {isSubmitting ? texts.savingButton : texts.saveButton}
                  </SaveButton>
                </FooterContainer>
              </form>
            )}
          </Formik>
        </>
      )}

      <ConfirmDialog
        isOpen={showSwitchDialog}
        onConfirm={handleConfirmSwitch}
        onCancel={handleCancelSwitch}
        dialogTexts={texts.switchTabDialog}
      />

      <ConfirmDialog
        isOpen={!!pendingDefaultFormId}
        onConfirm={handleConfirmSetDefault}
        onCancel={() => setPendingDefaultFormId(null)}
        dialogTexts={{
          ...texts.setDefaultDialog,
          content: format(texts.setDefaultDialog.content, {
            formName: ppfForms.find((form) => form.id === pendingDefaultFormId)?.formName || '',
          }),
        }}
      />

      <ConfirmDialog
        isOpen={showCreateDialog}
        onConfirm={handleCreateForm}
        onCancel={() => setShowCreateDialog(false)}
        confirmDisabled={!newFormName.trim()}
        dialogTexts={{
          ...texts.createDialog,
          content: (
            <TextField
              fullWidth
              autoFocus
              margin="dense"
              label={texts.createDialog.nameLabel}
              value={newFormName}
              onChange={(event) => setNewFormName(event.target.value)}
            />
          ),
        }}
      />
    </Container>
  );
};

export default compose(
  withExternalCoachContextReady,
  observer,
)(FormSettings);
